// src/data/shop/index.ts
// The shop's catalogue, read from game configuration (gameConfig/shopCategories and
// gameConfig/shopItems), which the owner edits in Admin → Pood. Nothing here is hard-coded.
import { getDomain } from '../../config/gameConfig';
import {
    BOOSTER_DEFAULTS, BOOSTER_SETTING, BoosterKind, ShopCategory, ShopItem, typeInfo,
} from '../../types/shop';

const byOrder = <T extends { order?: number; name?: string }>(a: T, b: T): number =>
    (a.order ?? 0) - (b.order ?? 0) || String(a.name ?? '').localeCompare(String(b.name ?? ''), 'et');

/** Every category, in the owner's order, switched on or not. */
export const getShopCategories = (): ShopCategory[] =>
    [...getDomain<ShopCategory>('shopCategories')].sort(byOrder);

/** Every product of a known type, in order. Entries of the pre-55.45 shape are ignored. */
export const getShopItems = (): ShopItem[] =>
    getDomain<ShopItem>('shopItems').filter((i) => !!typeInfo(i.type)).sort(byOrder);

/** What the shop sells right now: products switched on, in categories switched on. */
export function getItemsOnSale(): ShopItem[] {
    const open = new Set(getShopCategories().filter((c) => c.active !== false).map((c) => c.id));
    return getShopItems().filter((i) => i.active !== false && open.has(i.category));
}

interface EconomySetting { id: string; value: number }

/** Boosters a character may use per Estonian day, by kind — Admin → Seadistus. */
export function getBoosterLimits(): Record<BoosterKind, number> {
    const economy = getDomain<EconomySetting>('economy');
    const read = (kind: BoosterKind): number => {
        const raw = Number(economy.find((e) => e.id === BOOSTER_SETTING[kind])?.value);
        return Number.isFinite(raw) && raw > 0 ? raw : BOOSTER_DEFAULTS[kind];
    };
    return { work: read('work'), course: read('course'), clicks: read('clicks') };
}
